import React, { useState } from "react";
import { Select } from "@jsq/ds/core";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import { SubtaskRenderer } from "./index";
import type { SubtaskItem } from "../types";

interface DropdownProps {
  item: SubtaskItem;
  disabled?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({ item, disabled = false }) => {
  const derivedValue = typeof item.value === "string" ? item.value : "";
  const [selectedValue, setSelectedValue] = useState<string>(derivedValue);
  const [lastSeen, setLastSeen] = useState<{ id: SubtaskItem["id"]; value: string }>({
    id: item.id,
    value: derivedValue,
  });
  if (lastSeen.id !== item.id || lastSeen.value !== derivedValue) {
    setLastSeen({ id: item.id, value: derivedValue });
    setSelectedValue(derivedValue);
  }
  const { updateSubtaskValue } = useSubtaskActionsContext();

  const handleChange = (next: string | number | string[] | number[]) => {
    if (disabled) return;
    const value = Array.isArray(next) ? String(next[0] ?? "") : String(next);
    setSelectedValue(value);
    void updateSubtaskValue(item.id, value, !!item.isCompleted);
  };

  const options = item.options || [];
  const conditionalChildren = item.conditional_children;
  const activeChildren =
    conditionalChildren && selectedValue ? conditionalChildren[selectedValue] || [] : [];
  const displayLabel = item.isRequired ? `${item.label} *` : item.label;

  return (
    <div className={styles.subtaskItem}>
      <Select
        value={selectedValue}
        onChange={handleChange}
        label={displayLabel}
        helpText={item.helpText}
        disabled={disabled}
        placeholder="Select…"
        full
        data-testid={`dropdown-${item.id}`}
      >
        <Select.Section>
          {options.map((option) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select.Section>
      </Select>

      {activeChildren.length > 0 && (
        <div className={styles.childrenContainer}>
          {activeChildren.map((child) => (
            <SubtaskRenderer key={child.id} item={child} disabled={disabled} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
